import {Box, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {always} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useRenderCustomerMessageEmailTemplateV2Mutation} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, useDebouncedCallback, useDeepCompareEffect} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {isEmailHtmlEmpty} from '../../../utils/isEmailHtmlEmpty';
import {toEmailBodyPayload} from '../../../utils/toEmailBodyPayload';
import {FormValues} from '../types/SendOnlineOfferEmailFormSchema';
import {EmailWindow} from './EmailWindow';

type EmailPreviewProps = {
  businessCaseId: string;
  photoId: string | Nullish;
  sellerId: string;
  formApi: UseFormReturn<FormValues>;
  locale: string;
} & (
  | {
      customerMessageId: 'business_case-online_offer';
      /** Null only for a Business Case with offer alternatives but no deal vehicle yet. */
      vehicleId: string | null;
      onlineOfferUri: string | Nullish;
    }
  | {
      customerMessageId: 'business_case-buying_online_offer';
      vehicleId: string;
      offerId: string;
    }
  | {
      customerMessageId: 'business_case-combined_online_offer';
      vehicleId: string;
      buyingVehicleId: string;
      sellingOnlineOfferUri: string | Nullish;
      buyingOnlineOfferUri: string | Nullish;
    }
);

// A not-yet-created offer has no address. A fragment with no target keeps the preview's offer
// buttons clickable without navigating anywhere.
const PREVIEW_ONLINE_OFFER_URI = '#online-offer-preview';

export function EmailPreview(props: EmailPreviewProps) {
  const [renderCustomerMessageTemplate, {isLoading: isLoadingEmail}] =
    useRenderCustomerMessageEmailTemplateV2Mutation();

  const [htmlData, setHtmlData] = useState<string | null>(null);

  const data = useWatch({
    control: props.formApi.control,
    name: ['emailAddress', 'contactPerson', 'body'],
  });

  // A cleared editor holds `<p><br></p>`, which `isNilOrEmpty` reports as filled.
  const isAnyDataMissing = match([
    isNilOrEmpty(data[0]),
    isNilOrEmpty(data[1]),
    isEmailHtmlEmpty(data[2]),
  ])
    .with(
      // when emailAddress is missing, we cannot fetch
      [true, Pattern.boolean, Pattern.boolean],
      // when contactPerson is missing, we cannot fetch
      [Pattern.boolean, true, Pattern.boolean],
      // when body is missing, we cannot fetch
      [Pattern.boolean, Pattern.boolean, true],
      always(true)
    )
    .otherwise(always(false));

  const handleRenderEmail = () => {
    const {emailAddress, contactPerson, body} = props.formApi.getValues();

    if (isNilOrEmpty(emailAddress) || isNilOrEmpty(contactPerson) || isEmailHtmlEmpty(body)) {
      return;
    }

    // The preview must agree with the send path, or it shows the author something else than the
    // customer receives. The body and its format come from one place so they cannot drift apart.
    // `bodyFormat` sits beside `payload`, not inside it.
    const {body: normalizedBody, bodyFormat} = toEmailBodyPayload(body);

    const renderCustomerMessageRequestBodyV2 = match(props)
      .with({customerMessageId: 'business_case-buying_online_offer'}, (buyingProps) => ({
        customerId: contactPerson,
        locale: buyingProps.locale,
        bodyFormat,
        payload: {
          customerMessageId: buyingProps.customerMessageId,
          businessCaseId: buyingProps.businessCaseId,
          offerId: buyingProps.offerId,
          vehicleId: buyingProps.vehicleId,
          photoId: buyingProps.photoId ?? null,
          sellerId: buyingProps.sellerId,
          body: normalizedBody,
        },
      }))
      .with({customerMessageId: 'business_case-combined_online_offer'}, (combinedProps) => ({
        customerId: contactPerson,
        locale: combinedProps.locale,
        bodyFormat,
        payload: {
          customerMessageId: combinedProps.customerMessageId,
          businessCaseId: combinedProps.businessCaseId,
          sellingVehicleId: combinedProps.vehicleId,
          buyingVehicleId: combinedProps.buyingVehicleId,
          sellerId: combinedProps.sellerId,
          sellingOnlineOfferUri: combinedProps.sellingOnlineOfferUri ?? PREVIEW_ONLINE_OFFER_URI,
          buyingOnlineOfferUri: combinedProps.buyingOnlineOfferUri ?? PREVIEW_ONLINE_OFFER_URI,
          body: normalizedBody,
        },
      }))
      .with({customerMessageId: 'business_case-online_offer'}, (sellingProps) => ({
        customerId: contactPerson,
        locale: sellingProps.locale,
        bodyFormat,
        payload: {
          customerMessageId: sellingProps.customerMessageId,
          businessCaseId: sellingProps.businessCaseId,
          vehicleId: sellingProps.vehicleId,
          photoId: sellingProps.photoId ?? null,
          sellerId: sellingProps.sellerId,
          onlineOfferUri: sellingProps.onlineOfferUri ?? PREVIEW_ONLINE_OFFER_URI,
          body: normalizedBody,
        },
      }))
      .exhaustive();

    renderCustomerMessageTemplate({
      customerMessageId: props.customerMessageId,
      renderCustomerMessageRequestBodyV2,
    })
      .unwrap()
      .then((data) => data.body)
      .then(setHtmlData)
      .catch(handleApiError);
  };

  const debouncedHandleRenderEmail = useDebouncedCallback(handleRenderEmail, 1000);

  useDeepCompareEffect(() => {
    if (isAnyDataMissing) {
      setHtmlData(null);
      return;
    }

    debouncedHandleRenderEmail();
  }, [data]);

  return (
    <VStack height="100%">
      <Box position="relative" height="100%">
        <Show when={isNilOrEmpty(htmlData)}>
          <EmailPreviewButtonWrapper data-testid="customerEmailForm-email-window-empty-preview">
            <VStack align="center">
              <Text size="xSmall" align="center" color="tertiary">
                {i18n.t('entity.customer.labels.fillAllRequiredFields')}
              </Text>
            </VStack>
          </EmailPreviewButtonWrapper>
        </Show>
        <EmailWindow
          htmlData={htmlData}
          isRerendering={isLoadingEmail && isNotNilOrEmpty(htmlData)}
          data-testid="customerEmailForm-email-window"
        />
      </Box>
    </VStack>
  );
}

const EmailPreviewButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
